import { Heading } from "components/designV2/heading/heading";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { mediaMax } from "components/designV2/breakpoints";
import { Radius, Spacing } from "components/designV2/constants";

export const SettingsV2Shell = styled.main`
  background: ${ColorV2.Blue7};
  flex: 1;
  min-height: calc(100dvh - 104px);
  padding: ${Spacing.lg}px ${Spacing.lg}px 48px;

  ${mediaMax.tablet} {
    padding: ${Spacing.md}px ${Spacing.md}px 48px;
  }
`;

export const SettingsV2Layout = styled.div`
  display: grid;
  gap: ${Spacing.lg}px;
  grid-template-columns: 272px minmax(0, 1fr);
  width: 100%;

  ${mediaMax.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const SettingsV2Sidebar = styled.aside`
  align-self: start;
  background: ${ColorV2.White};
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - 152px);
  padding: ${Spacing.md}px;
  position: sticky;
  top: ${Spacing.md}px;

  ${mediaMax.tablet} {
    min-height: auto;
    position: static;
  }
`;

export const SettingsV2Menu = styled.nav`
  border-bottom: 1px solid ${ColorV2.Blue7};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  padding-bottom: 64px;
`;

export const SettingsV2MenuButton = styled.button<{ $isActive: boolean }>`
  ${TypographyV2.b1}
  align-items: center;
  background: transparent;
  border: none;
  border-radius: ${Radius.md}px;
  color: ${(props) => (props.$isActive ? ColorV2.Blue1 : ColorV2.Blue5)};
  cursor: pointer;
  display: flex;
  gap: ${Spacing.md}px;
  min-height: 48px;
  padding: ${Spacing.sm}px;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${ColorV2.Blue7};
    color: ${ColorV2.Blue1};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:disabled:hover {
    background: transparent;
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$isActive &&
    css`
      background: ${ColorV2.Blue7};

      &::before {
        background: ${ColorV2.Blue1};
        border-radius: 999px;
        content: "";
        height: 20px;
        margin-right: -${Spacing.sm}px;
        width: 2px;
      }
    `}
`;

export const SettingsV2MenuLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SettingsV2Support = styled.button`
  align-items: center;
  background: ${ColorV2.SimflexityGreen};
  border: none;
  border-radius: 24px;
  color: ${ColorV2.White};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  margin-top: auto;
  padding: 28px 18px 24px;
  text-align: center;
  width: 100%;

  &:hover {
    opacity: 0.9;
  }
`;

export const SettingsV2SupportTitle = styled.span`
  ${TypographyV2.b1}
  display: block;
  margin-bottom: 12px;
`;

export const SettingsV2SupportText = styled.span`
  ${TypographyV2.s1}
  display: block;
  margin-bottom: 24px;
`;

export const SettingsV2SupportButton = styled.span`
  ${TypographyV2.b1}
  align-items: center;
  background: ${ColorV2.Blue1};
  border-radius: 999px;
  color: ${ColorV2.White};
  display: inline-flex;
  gap: 12px;
  justify-content: center;
  min-height: 48px;
  padding: 0 24px;
  white-space: nowrap;
`;

export const SettingsV2SupportButtonIcon = styled.span`
  align-items: center;
  display: inline-flex;
  justify-content: center;
`;

export const SettingsV2Content = styled.section<{ $isFrameless?: boolean }>`
  background: ${(props) =>
    props.$isFrameless ? "transparent" : ColorV2.White};
  border-radius: ${(props) => (props.$isFrameless ? "0" : "24px")};
  min-height: 520px;
  min-width: 0;
  padding: ${(props) =>
    props.$isFrameless ? "0" : `clamp(${Spacing.md}px, 3vw, 48px)`};

  ${mediaMax.mobile} {
    border-radius: ${(props) => (props.$isFrameless ? "0" : `${Radius.lg}px`)};
    padding: ${(props) => (props.$isFrameless ? "0" : `${Spacing.md}px`)};
  }
`;

export const SettingsV2PageTitle = styled(Heading).attrs({
  color: ColorV2.Blue1,
  level: "h3",
})`
  margin: 0;
`;
